import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useRouter, isStaffUser, isProd, getGoogleMapsLinkByLocationQuery, useTerminology } from "utils";
import PageHeaderTabs from "../../health-safety-forms/components/page-header-tabs";
import { Grid, Typography, Link, TextField, Card, CardContent, CardHeader, Button } from "@material-ui/core";
import { DatePicker, KeyboardTimePicker } from "@material-ui/pickers";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	PopupSnackbar,
	FormActions,
	FormButton,
	FormSubmitButton,
	SelectInput,
	StaffName,
	StyledModal,
} from "frame/components";
import { strip, SiteForm } from "features/forms";
import { getCurrentPosition } from "features/geolocation";
import { TimesheetForm } from "./timesheet-form";
import { TimesheetToggleForm } from "./timesheet-toggle-form";
import ReactGA from "react-ga";
import { connect } from "react-redux";
import {
	LIST_TIMESHEETS_ROUTE,
	ADD_ADMIN_TIMESHEET_ROUTE,
	ADD_TIMESHEET_ROUTE,
	NEW_LEAVE_PATH
} from "../timesheet-routes";
import {
	onAddTimesheetValueChange,
	onAddTimesheetReset,
	onAddTimesheetSave,
	onAddTimesheetStartTimer,
	onConfirmActiveTimesheet,
	onDiscardActiveTimesheet
} from "../timesheet-actions";
import {
	onAddAdminTimesheetValueChange,
	onAddAdminTimesheetSave,
	onAddAdminTimesheetReset
} from "../timesheet-admin-actions";
import {
	TIMESHEET_OVERLAP_WARNING_MESSAGE,
	checkTimesheetOverlap,
	timesheetFormRangeToUtcIso,
} from "../timesheet-overlap-check";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { onListRequested as onSitesListRequested } from "features/sites";
import { SITES_ROUTE } from "../../sites/actions/types";
import SitePinnedItems from "../../sites/components/site-pinned-items";
import { isInImpersonationMode } from "frame/access-token";

const getEffectiveSiteId = (siteId) =>
	siteId && siteId !== "" && siteId !== "NO-ID" ? siteId : null;

const getKnownPinnedCount = (siteList, siteId) => {
	const effectiveSiteId = getEffectiveSiteId(siteId);
	if (!effectiveSiteId || !siteList?.length) {
		return undefined;
	}
	const site = siteList.find((s) => s.id === effectiveSiteId);
	if (!site || site.pinnedJobCount == null) {
		return undefined;
	}
	return site.pinnedJobCount;
};

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(-2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	actions: {
		marginTop: theme.spacing(3),
		paddingBottom: theme.spacing(8)
	},
	menu: {
		"padding": theme.spacing(3, 0, 0, 0),
		"& > *": {
			marginRight: theme.spacing(3)
		}
	},
	label: { color: "#999999", marginBottom: theme.spacing(0.5), lineHeight: 1.2 },
	mapsLink: { color: theme.palette.secondary.dark },
	field: {
		width: "100%"
	},
	root: {
		margin: theme.spacing(2, 0)
	},
	inlineStartRow: {
		display: "flex",
		alignItems: "stretch",
		gap: theme.spacing(1),
		[theme.breakpoints.down("xs")]: {
			flexDirection: "column",
		},
	},
	inlineStartButton: {
		flex: "1 1 auto",
		minWidth: 160,
		background: theme.palette.success.main,
		borderColor: theme.palette.success.main,
		color: theme.palette.getContrastText(theme.palette.success.main),
		"&:hover": {
			background: theme.palette.success.dark,
			borderColor: theme.palette.success.dark,
		},
	},
	inlineStartHint: {
		flex: "1 1 auto",
		display: "flex",
		alignItems: "center",
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
		minHeight: 56,
	}
}));
																																																																					
const mapStateToProps = ({ timesheetFormReducer, timesheetAdminFormReducer, profile }) => ({
	values: timesheetFormReducer,
	valuesAdmin: timesheetAdminFormReducer,
	isValid: timesheetAdminFormReducer.isValid,
	role: profile.role
});
																																																																					
const mapDispatchToProps = {
	onChange: onAddTimesheetValueChange,
	onChangeAdmin: onAddAdminTimesheetValueChange,
	onReset: onAddTimesheetReset,
	onResetAdmin: onAddAdminTimesheetReset,
	onSave: onAddTimesheetSave,
	onSaveAdmin: onAddAdminTimesheetSave,
	onStartTimer: onAddTimesheetStartTimer,
	onConfirmActive: onConfirmActiveTimesheet,
	onDiscardActive: onDiscardActiveTimesheet
};
																																																																					
export const AddTimesheetForm = connect(
	mapStateToProps,
	mapDispatchToProps
)(
	({
		values,
		valuesAdmin,
		onChange,
		onChangeAdmin,
		onReset,
		onResetAdmin,
		onSave,
		onSaveAdmin,
		onStartTimer,
		onConfirmActive,
		onDiscardActive,
		isValid,
		role
	}) => {
		const classes = useStyles();
		const dispatch = useDispatch();
		const { history } = useRouter();
		const { siteLabel } = useTerminology();
		const [reviewMode, setReviewMode] = useState(false);
		const [overlapDialogOpen, setOverlapDialogOpen] = useState(false);
		const [overlapMessage, setOverlapMessage] = useState(TIMESHEET_OVERLAP_WARNING_MESSAGE);
		const [checkingOverlap, setCheckingOverlap] = useState(false);
		const pendingSaveKindRef = useRef(null);
		const staffList = useSelector(({ staff }) => staff.pagination?.results);
		const currentStaffId = useSelector(({ profile }) => profile?.staffId);
		const currentOrganisationGUID = useSelector(
			({ organisations }) =>
				organisations?.currentOrganisation?.id ||
				organisations?.currentOrganisation ||
				""
		);
		const siteList = useSelector(({ sites }) => sites.pagination?.results || []);
		const hasNetwork = useSelector(({ queue }) => queue.hasNetwork);
		const taskTypeList = useSelector(({ taskTypes }) => taskTypes.data || []);
		const featureFlags = useSelector(({ featureFlags }) => featureFlags);
		const manualTimesheetEntriesEnabled = featureFlags?.manualTimesheetEntries === true;
		// Timer entries are always available; the dedicated Timer Timesheet Entries flag has been removed.
		const timerTimesheetEntriesEnabled = true;
		const { location } = useRouter();

		
		// Check if we're in impersonation mode
		const isImpersonating = isInImpersonationMode();
		
		// When impersonating, we should respect the impersonated user's role
		// For button visibility and form access, check the actual role (not impersonation status)
		const isAdmin = !isStaffUser(role);
		const isStaff = isStaffUser(role);
		
		let form = <TimesheetToggleForm />;
		let isTimesheetForm = useRef(false);
		const { match } = useRouter();
		const { siteId } = match.params;
		
		// Check if we're on the admin page or if the user has admin rights
		// In impersonation mode, we should respect the impersonated user's role
		// Admin form should only be accessible to admins, and only when explicitly on the admin route.
		const useAdminForm =
			location.pathname === ADD_ADMIN_TIMESHEET_ROUTE ||
			location.pathname === `${ADD_ADMIN_TIMESHEET_ROUTE}/${siteId}`;

		const renderSitePinned = (selectedSiteId) => {
			const effectiveSiteId = getEffectiveSiteId(selectedSiteId);
			if (!effectiveSiteId || !currentOrganisationGUID) {
				return null;
			}
			return (
				<SitePinnedItems
					siteId={effectiveSiteId}
					organisationId={currentOrganisationGUID}
					variant="compact"
					defaultExpanded={false}
					allowUnpin={false}
					knownPinnedCount={getKnownPinnedCount(siteList, selectedSiteId)}
				/>
			);
		};

		useEffect(() => {
			if (hasNetwork && siteList.length === 0) {
				dispatch(onSitesListRequested());
			}
		}, [dispatch, hasNetwork, siteList.length]);

		// Guard against accidental double submits (double-click / enter + click)
		const submitLockRef = useRef(false);

		useEffect(() => {
			if (!values?.isLoading && !valuesAdmin?.isLoading && !checkingOverlap) {
				submitLockRef.current = false;
			}
		}, [values?.isLoading, valuesAdmin?.isLoading, checkingOverlap]);

		const proceedWithManualSave = (kind) => {
			if (kind === "admin") {
				onSaveAdmin(strip(valuesAdmin));
				onResetAdmin();
			} else {
				onSave(strip(values));
			}
		};

		const onSubmit = async (event) => {
			event.preventDefault();
			if (submitLockRef.current || checkingOverlap) return;
			submitLockRef.current = true;
			isProd &&
				ReactGA.event({
					category: "Tasks",
					action: "Save Clicked",
					label: "Add Timesheet"
				});

			// Stop/confirm review saves an already-started entry — no overlap prompt here.
			if (!isTimesheetForm.current && reviewMode) {
				onConfirmActive(strip(values));
				return;
			}

			const kind = isTimesheetForm.current ? "admin" : "staff";
			const formValues = kind === "admin" ? valuesAdmin : values;
			const range = timesheetFormRangeToUtcIso(formValues?.timesheet, moment);
			const staffId = formValues?.timesheet?.staffId;
			const excludeTimesheetId = formValues?.timesheet?.id || undefined;

			if (!range || !staffId) {
				proceedWithManualSave(kind);
				return;
			}

			setCheckingOverlap(true);
			const result = await checkTimesheetOverlap({
				organisationId: currentOrganisationGUID,
				staffId,
				startDateTimeUTC: range.startDateTimeUTC,
				endDateTimeUTC: range.endDateTimeUTC,
				excludeTimesheetId,
				hasNetwork,
			});
			setCheckingOverlap(false);

			if (result.hasOverlap) {
				submitLockRef.current = false;
				pendingSaveKindRef.current = kind;
				setOverlapMessage(result.message || TIMESHEET_OVERLAP_WARNING_MESSAGE);
				setOverlapDialogOpen(true);
				return;
			}

			proceedWithManualSave(kind);
		};

		const onConfirmOverlapSave = () => {
			const kind = pendingSaveKindRef.current || (isTimesheetForm.current ? "admin" : "staff");
			pendingSaveKindRef.current = null;
			setOverlapDialogOpen(false);
			submitLockRef.current = true;
			proceedWithManualSave(kind);
		};

		// StyledModal calls onClose after onConfirm; only unlock when Cancel left a pending save.
		const onCloseOverlapDialog = () => {
			const hadPending = pendingSaveKindRef.current != null;
			pendingSaveKindRef.current = null;
			setOverlapDialogOpen(false);
			if (hadPending) {
				submitLockRef.current = false;
			}
		};

		const onCancel = () => {
			if (isTimesheetForm.current) {
				onResetAdmin();
			} else {
				onReset();
			}
			history.push(LIST_TIMESHEETS_ROUTE);
		};

		const onDiscardAdmin = () => {
			// For admins discarding someone else's timesheet, use the same discard logic as users
			if (valuesAdmin.timesheet.id) {
				// If we have an ID, use the API discard (delete the timesheet)
				onDiscardActive(valuesAdmin.timesheet.id);
			} else {
				// Just reset and navigate back if no ID
				onResetAdmin();
				history.push(LIST_TIMESHEETS_ROUTE);
			}
			setReviewMode(false);
		};

		const onDiscard = () => {
			// Check if there's an offline snapshot even if no ID
			const hasOfflineSnapshot = (() => {
				try {
					const raw = localStorage.getItem("offline_active_timesheet");
					return !!raw && raw !== "null";
				} catch (e) {
					return false;
				}
			})();

			if (values.timesheet.id) {
				// If we have an ID, use the API discard
				onDiscardActive(values.timesheet.id);
			} else if (hasOfflineSnapshot) {
				// If no ID but there's an offline snapshot, clear it and reset
				try {
					localStorage.removeItem("offline_active_timesheet");
				} catch (e) {
					// ignore storage errors
				}
				onReset();
			} else {
				// Just reset the form if no ID and no snapshot
				onReset();
			}
			history.push(LIST_TIMESHEETS_ROUTE);
			setReviewMode(false);
		};

		// Admin users can access the admin form; everyone else uses the standard add-timesheet flow.
		if (useAdminForm && isAdmin) {
			form = <TimesheetForm values={valuesAdmin} onChange={onChangeAdmin} showStaffDropdown={true} />;
			isTimesheetForm.current = true;
		} else {
			form = <TimesheetToggleForm />;
			isTimesheetForm.current = false;
		}

		// Hard guard: if a non-admin lands on the admin route, redirect to the standard add-timesheet route.
		useEffect(() => {
			const isOnAdminRoute =
				location.pathname === ADD_ADMIN_TIMESHEET_ROUTE ||
				location.pathname === `${ADD_ADMIN_TIMESHEET_ROUTE}/${siteId}` ||
				location.pathname.startsWith(`${ADD_ADMIN_TIMESHEET_ROUTE}/`);

			if (!isAdmin && isOnAdminRoute) {
				history.replace(siteId ? `${ADD_TIMESHEET_ROUTE}/${siteId}` : ADD_TIMESHEET_ROUTE);
			}
		}, [isAdmin, location.pathname, siteId, history]);

	useLayoutEffect(() => {
		// Always pre-populate the regular timesheet form when there's no active timesheet.
		if (!values.timesheet.toggle) {
			onReset({
				timesheet: {
					staffId: staffList?.some((staff) => staff.id === currentStaffId) ? currentStaffId : "",
					startDate: moment().format("YYYY-MM-DD"),
					startTime: moment().format("HH:mm"),
					endTime: ""
				}
			});
		}

		const cloned = (location && location.state && location.state.cloneTimesheet) ? location.state.cloneTimesheet : null;
		const stopTimesheet = (location && location.state && location.state.stopTimesheet) ? location.state.stopTimesheet : null;
		const scheduleStart = (location && location.state && location.state.scheduleStart) ? location.state.scheduleStart : null;

		if (scheduleStart) {
			// Schedule handoff: prefill timer form from a schedule entry for quick start.
			onReset({
				siteId: scheduleStart.siteId || "",
				timesheet: {
					...values.timesheet,
					startDate: moment(scheduleStart.startsAtUTC || undefined)
						.local()
						.format("YYYY-MM-DD"),
					startTime: moment(scheduleStart.startsAtUTC || undefined)
						.local()
						.format("HH:mm"),
					notes: scheduleStart.notes || scheduleStart.title || "",
				},
			});
		} else if (cloned) {
			// Clone: populate admin form directly with cloned values in a single dispatch
			// (avoids an intermediate render with empty siteId that hides the TimesheetForm)
			const clonedStaffId = isStaff && currentStaffId ? currentStaffId : (cloned.staffId || "");
		onResetAdmin({
			siteId: cloned.siteId,
			organisationTaskTypeId: cloned.organisationTaskTypeId || "",
			equipment: cloned.equipment || [],
			timesheet: {
				staffId: clonedStaffId,
				startDate: cloned.startDateTimeUTC ? moment.utc(cloned.startDateTimeUTC).local().format("YYYY-MM-DD") : "",
				startTime: cloned.startDateTimeUTC ? moment.utc(cloned.startDateTimeUTC).local().format("HH:mm") : "",
				endDate: cloned.endDateTimeUTC ? moment.utc(cloned.endDateTimeUTC).local().format("YYYY-MM-DD") : "",
				endTime: cloned.endDateTimeUTC ? moment.utc(cloned.endDateTimeUTC).local().format("HH:mm") : "",
				lunchTime: cloned.lunchTimeMinutes ? String(cloned.lunchTimeMinutes) : "0",
				notes: cloned.notes || "",
				startLatitude: cloned.startLatitude || null,
				startLongitude: cloned.startLongitude || null,
				endLatitude: cloned.endLatitude || null,
				endLongitude: cloned.endLongitude || null,
				organisationTaskTypeId: cloned.organisationTaskTypeId || "",
			}
		});
		} else if (stopTimesheet) {
			// Stop timesheet: populate admin form with the timesheet being stopped
		onResetAdmin({
			siteId: stopTimesheet.siteId,
			organisationTaskTypeId: stopTimesheet.organisationTaskTypeId || "",
			equipment: stopTimesheet.equipment || [],
			timesheet: {
				id: stopTimesheet.id,
				staffId: stopTimesheet.staffId || "",
				startDate: stopTimesheet.startDateTimeUTC ? moment.utc(stopTimesheet.startDateTimeUTC).local().format("YYYY-MM-DD") : "",
				startTime: stopTimesheet.startDateTimeUTC ? moment.utc(stopTimesheet.startDateTimeUTC).local().format("HH:mm") : "",
				endDate: stopTimesheet.endDateTimeUTC ? moment.utc(stopTimesheet.endDateTimeUTC).local().format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
				endTime: stopTimesheet.endDateTimeUTC ? moment.utc(stopTimesheet.endDateTimeUTC).local().format("HH:mm") : moment().format("HH:mm"),
				lunchTime: stopTimesheet.lunchTimeMinutes ? String(stopTimesheet.lunchTimeMinutes) : "0",
				notes: stopTimesheet.notes || "",
				startLatitude: stopTimesheet.startLatitude || null,
				startLongitude: stopTimesheet.startLongitude || null,
				endLatitude: stopTimesheet.endLatitude || null,
				endLongitude: stopTimesheet.endLongitude || null,
				organisationTaskTypeId: stopTimesheet.organisationTaskTypeId || "",
				toggle: stopTimesheet.isActive || false
			}
		});
		} else {
			// New timesheet: populate admin form with today's date/time defaults
			const defaultStaffId = isStaff && currentStaffId ? currentStaffId : 
				(staffList?.some((staff) => staff.id === currentStaffId) ? currentStaffId : "");
			onResetAdmin({
				timesheet: {
					staffId: defaultStaffId,
					startDate: moment().format("YYYY-MM-DD"),
					endDate: moment().format("YYYY-MM-DD"),
					startTime: moment().format("HH:mm"),
					endTime: moment().format("HH:mm")
				}
			});
		}

		// Initialise review mode from URL (?stopped)
		const params = new URLSearchParams(location.search || "");
		setReviewMode(params.has("stopped"));
	}, []);

		// Keep review mode in sync with URL changes so back/forward work
		useEffect(() => {
			const params = new URLSearchParams(location.search || "");
			setReviewMode(params.has("stopped"));
		}, [location.search]);

		// Check if timer timesheet entries are disabled and block access to timer route
		useEffect(() => {
			const isTimerRoute = 
				location.pathname === ADD_TIMESHEET_ROUTE ||
				location.pathname === `${ADD_TIMESHEET_ROUTE}/${siteId}` ||
				location.pathname.startsWith(`${ADD_TIMESHEET_ROUTE}/`);
			
			// Timer route is always available; no redirect based on a Timer feature flag.
		}, [location.pathname, useAdminForm, siteId, history]);

		useEffect(() => {
			if (!siteId) {
				return;
			}

			if (isTimesheetForm.current) {
				// For staff users, ensure staffId is locked to their own ID
				const updatedTimesheet = isStaff && currentStaffId 
					? { ...valuesAdmin.timesheet, staffId: currentStaffId }
					: { ...valuesAdmin.timesheet };
				onChangeAdmin({ ...valuesAdmin, siteId: siteId, timesheet: updatedTimesheet });
			} else {
				onChange({ ...values, siteId: siteId, timesheet: { ...values.timesheet } });
			}
		}, [isTimesheetForm.current, siteId, isStaff, currentStaffId]);

		// Local values
		const timesheet = values.timesheet || {};
		// When Manual Timesheet Entries is enabled, the inline manual fields and save option
		// should be available as the standard experience on /add-timesheet.
		const canUseInlineManual = manualTimesheetEntriesEnabled;
		const hasInlineSaveRequirements =
			!!values.siteId &&
			!!timesheet.startDate &&
			!!timesheet.startTime &&
			!!timesheet.endDate &&
			!!timesheet.endTime &&
			!values.isEndDateTimeInvalid;

		const shouldShowInlineSaveActions =
			!isTimesheetForm.current &&
			(reviewMode || (canUseInlineManual && !values.timesheet.toggle));

		const inlineSaveDisabled = values.isLoading || !hasInlineSaveRequirements;
		const inlineSaveMissingFields = (() => {
			if (values.isLoading) return [];
			const missing = [];
			if (!values.siteId) missing.push(siteLabel);
			if (!timesheet.startDate) missing.push("Start Date");
			if (!timesheet.startTime) missing.push("Start Time");
			if (!timesheet.endDate) missing.push("End Date");
			if (!timesheet.endTime) missing.push("End Time");
			return missing;
		})();

	const workTypesEnabled = featureFlags?.workTypes !== false; // default true
	const hasTaskTypes = Array.isArray(taskTypeList) && taskTypeList.length > 0;
	const taskTypeRequired = workTypesEnabled && hasTaskTypes;

	// Keep taskTypeRequired in the admin form reducer in sync so isValid gates properly
	useEffect(() => {
		if (valuesAdmin.taskTypeRequired !== taskTypeRequired) {
			onChangeAdmin({ ...valuesAdmin, taskTypeRequired });
		}
	}, [taskTypeRequired]);

	const isInlineStartDisabled =
			isStarting ||
			values.isLoading ||
			!values.siteId ||
			(workTypesEnabled && hasTaskTypes && !values.organisationTaskTypeId);

	const [isStarting, setIsStarting] = useState(false);

	const handleInlineStart = async () => {
		if (isStarting || values.isLoading) return;
		setIsStarting(true);
		try {
			const { latitude, longitude } = await getCurrentPosition();
			onStartTimer({
				staffId: currentStaffId,
				latitude,
				longitude,
			});
			// Redux isLoading is set synchronously; local busy can clear
			setIsStarting(false);
		} catch (e) {
			setIsStarting(false);
		}
	};

		return (
			<Page title="+ Timesheet">
				<ScrollToTopOnMount />
				<PageHeaderTabs
					title={reviewMode ? "Review and Save Timesheet" : (values.timesheet.toggle ? "Active Timesheet" : (isTimesheetForm.current ? (isStaff ? "+ Timesheet (Manual)" : "+ Timesheet (Admin)") : "+ Timesheet"))}
					staffUserHide={false}
					addTabTitle={values.timesheet.toggle ? "Active Timesheet" : "+ Timesheet"}
					hideAddTabIcon={!!values.timesheet.toggle}
					hideAddTabButton={true}
					hideListTabButton={true}
					addTabPath={siteId ? `${ADD_TIMESHEET_ROUTE}/${siteId}` : ADD_TIMESHEET_ROUTE}
					listTabPath={LIST_TIMESHEETS_ROUTE}
					showAddAdminButton={isAdmin && !useAdminForm && !reviewMode}
					addAdminPath={siteId ? `${ADD_ADMIN_TIMESHEET_ROUTE}/${siteId}` : ADD_ADMIN_TIMESHEET_ROUTE}
					showLeaveButton={false}
					newLeavePath={NEW_LEAVE_PATH}
					backActionPath={siteId ? `${SITES_ROUTE}/${siteId}` : LIST_TIMESHEETS_ROUTE}
				/>
				<PageContent padSides padBottom>
					<ValidatorForm onSubmit={onSubmit} name="add-timesheet" className={classes.root}>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								{isTimesheetForm.current ? (
									<>
										<SiteForm
											disableSite={false}
											disableDate={isStaffUser(role)}
											hideDate
											values={valuesAdmin}
											onChange={onChangeAdmin}
											searchDropdownReadOnly={true}
											isAdmin={true}
											hideTaskTypes={false}
											hideEquipment={false}
											allowEquipmentUsage={true}
										/>
										{renderSitePinned(valuesAdmin.siteId)}
										{valuesAdmin.siteId && valuesAdmin.siteId !== "" && form}
									</>
								) : (
									<>
										{/* Site, Work Type, Equipment — optional fields stay above Start/Stop */}
										<SiteForm
											disableSite={values.timesheet.toggle && !reviewMode}
											disableDate={isStaffUser(role)}
											hideDate
											values={values}
											onChange={onChange}
											searchDropdownReadOnly={!reviewMode}
											hideTaskTypes={false}
											hideEquipment={false}
											allowEquipmentUsage={!!reviewMode}
										/>
										{renderSitePinned(values.siteId)}

										{!reviewMode && (
											<div style={{ marginTop: 16 }}>
												{canUseInlineManual && !values.timesheet.toggle ? (
													<div className={classes.inlineStartRow}>
														<Button
															type="button"
															variant="contained"
															disableElevation
															className={classes.inlineStartButton}
															onClick={handleInlineStart}
															disabled={isInlineStartDisabled}
														>
															{isStarting || values.isLoading ? "STARTING…" : "START"}
														</Button>
														<div className={classes.inlineStartHint}>
															<Typography variant="body1">
																OR complete and <strong>SAVE</strong> below
															</Typography>
														</div>
													</div>
												) : (
													<TimesheetToggleForm
														actionsOnly
														onStopped={() => {
															const basePath = siteId ? `${ADD_TIMESHEET_ROUTE}/${siteId}` : ADD_TIMESHEET_ROUTE;
															history.push(`${basePath}?stopped=1`);
														}}
													/>
												)}
											</div>
										)}

										{/* Timesheet Date/Time Fields Card */}
										{reviewMode ? (
											<Card className={classes.root} style={{ marginTop: 16 }}>
												<CardHeader
													title="Timesheet"
													titleTypographyProps={{ variant: "h4" }}
												/>
												<CardContent>
													<Grid container spacing={2}>
													{/* Start Date/Time - Editable for staff when Manual Timesheet Entries is enabled */}
													{manualTimesheetEntriesEnabled && isStaff ? (
														<>
															<Grid item sm={4} xs={12}>
																<DatePicker
																	format="Do MMMM YYYY"
																	fullWidth
																	inputVariant="outlined"
																	label="Start Date *"
																	showTodayButton
																	value={timesheet.startDate ? moment(timesheet.startDate, "YYYY-MM-DD") : null}
																	variant="outlined"
																	disableFuture
																	onChange={(value) => onChange({ ...values, timesheet: { ...timesheet, startDate: value ? value.format("YYYY-MM-DD") : "" } })}
																	error={values.isEndDateTimeInvalid}
																	autoOk
																	okLabel=""
																/>
															</Grid>
															<Grid item sm={4} xs={12}>
																<KeyboardTimePicker
																	fullWidth
																	inputVariant="outlined"
																	label="Start Time *"
																	value={timesheet.startTime ? moment(timesheet.startTime, "HH:mm") : null}
																	onChange={(value) =>
																		onChange({
																			...values,
																			timesheet: {
																				...timesheet,
																				startTime:
																					value &&
																					typeof value.format === "function" &&
																					typeof value.isValid === "function" &&
																					value.isValid()
																						? value.format("HH:mm")
																						: ""
																			}
																		})
																	}
																	error={values.isEndDateTimeInvalid}
																	ampm
																	minutesStep={5}
																	keyboardIcon={<AccessTimeIcon fontSize="small" />}
																	KeyboardButtonProps={{ "aria-label": "change start time" }}
																/>
															</Grid>
															<Grid item sm={4} xs={12}>
																<DatePicker
																	format="Do MMMM YYYY"
																	fullWidth
																	inputVariant="outlined"
																	label="End Date *"
																	minDate={timesheet.startDate ? moment(timesheet.startDate, "YYYY-MM-DD").startOf("day") : null}
																	showTodayButton
																	value={timesheet.endDate ? moment(timesheet.endDate, "YYYY-MM-DD") : null}
																	variant="outlined"
																	onChange={(value) => onChange({ ...values, timesheet: { ...timesheet, endDate: value ? value.format("YYYY-MM-DD") : "" } })}
																	error={values.isEndDateTimeInvalid}
																	autoOk
																	okLabel=""
																/>
															</Grid>
															<Grid item sm={4} xs={12}>
																<KeyboardTimePicker
																	fullWidth
																	inputVariant="outlined"
																	label="End Time *"
																	value={timesheet.endTime ? moment(timesheet.endTime, "HH:mm") : null}
																	onChange={(value) =>
																		onChange({
																			...values,
																			timesheet: {
																				...timesheet,
																				endTime:
																					value &&
																					typeof value.format === "function" &&
																					typeof value.isValid === "function" &&
																					value.isValid()
																						? value.format("HH:mm")
																						: ""
																			}
																		})
																	}
																	error={values.isEndDateTimeInvalid}
																	ampm
																	minutesStep={5}
																	keyboardIcon={<AccessTimeIcon fontSize="small" />}
																	KeyboardButtonProps={{ "aria-label": "change end time" }}
																/>
																{values.isEndDateTimeInvalid && (
																	<Typography variant="body2" style={{ color: "#f44336", marginTop: 4 }}>
																		End date/time must be later than start
																	</Typography>
																)}
															</Grid>
															<Grid item sm={4} xs={12}>
																<SelectInput
																	id="lunch_time"
																	label="Breaks"
																	options={[{ label: "None", value: "0" }, { label: "30 mins", value: "30" }, { label: "45 mins", value: "45" }, { label: "60 mins", value: "60" }]}
																	value={timesheet.lunchTime || "0"}
																	onChange={(v) => onChange({ ...values, timesheet: { ...timesheet, lunchTime: v } })}
																	noNone
																/>
															</Grid>
														</>
													) : (
														<>
															<Grid item xs={6}>
																<Typography className={classes.label} variant="body2">Started</Typography>
																<Typography variant="h5">
																	{moment(timesheet.startDate + ' ' + timesheet.startTime, "YYYY-MM-DD HH:mm").format("h:mm a")}
																</Typography>
																<Typography variant="subtitle1">
																	{moment(timesheet.startDate + ' ' + timesheet.startTime, "YYYY-MM-DD HH:mm").format("D MMM YYYY")}
																</Typography>
															</Grid>
															<Grid item xs={6}>
																<Typography className={classes.label} variant="body2">Ended</Typography>
																<Typography variant="h5">
																	{moment(timesheet.endDate + ' ' + timesheet.endTime, "YYYY-MM-DD HH:mm").format("h:mm a")}
																</Typography>
																<Typography variant="subtitle1">
																	{moment(timesheet.endDate + ' ' + timesheet.endTime, "YYYY-MM-DD HH:mm").format("D MMM YYYY")}
																</Typography>
															</Grid>
														</>
													)}
													{/* Hide "View on Maps" when fields are editable */}
													{!(manualTimesheetEntriesEnabled && isStaff) && (
														<>
															<Grid item xs={6}>
																<Typography variant="body2" className={classes.label}>&nbsp;</Typography>
																<Typography variant="h5">
																	{timesheet.startLatitude && timesheet.startLongitude ? (
											<Link href={getGoogleMapsLinkByLocationQuery(timesheet.startLatitude, timesheet.startLongitude)} className={classes.mapsLink} color="inherit" variant="inherit" target="_blank" rel="noreferrer">View on Maps</Link>
																	) : ("N/A")}
																</Typography>
															</Grid>
															<Grid item xs={6}>
																<Typography variant="body2" className={classes.label}>&nbsp;</Typography>
																<Typography variant="h5">
																	{timesheet.endLatitude && timesheet.endLongitude ? (
												<Link href={getGoogleMapsLinkByLocationQuery(timesheet.endLatitude, timesheet.endLongitude)} className={classes.mapsLink} color="inherit" variant="inherit" target="_blank" rel="noreferrer">View on Maps</Link>
																	) : ("N/A")}
																</Typography>
															</Grid>
														</>
													)}
													{/* When fields are editable, Breaks is already shown next to the date/time inputs. */}
													{!(manualTimesheetEntriesEnabled && isStaff) && (
														<Grid item md={6} xs={12}>
															<SelectInput
																id="lunch_time"
																label="Breaks"
																options={[{ label: "None", value: "0" }, { label: "30 mins", value: "30" }, { label: "45 mins", value: "45" }, { label: "60 mins", value: "60" }]}
																value={timesheet.lunchTime || "0"}
																onChange={(v) => onChange({ ...values, timesheet: { ...timesheet, lunchTime: v } })}
																noNone
															/>
														</Grid>
													)}
													<Grid item xs={12}>
															<TextField
																id="timesheet_notes"
																fullWidth
																label="Notes"
																multiline
																rows="3"
																value={timesheet.notes || ""}
																variant="outlined"
																onChange={(e) => onChange({ ...values, timesheet: { ...timesheet, notes: e.target.value } })}
															/>
														</Grid>
													</Grid>
												</CardContent>
											</Card>
										) : (
											<>
												{/* When manual timesheet entries are enabled for staff, allow inline manual entry on the timer page. */}
												{canUseInlineManual && !values.timesheet.toggle && (
													<Card className={classes.root} style={{ marginTop: 16 }}>
														<CardContent>
															<Grid container spacing={2}>
																<Grid item sm={6} xs={12}>
																	<DatePicker
																		format="Do MMMM YYYY"
																		fullWidth
																		inputVariant="outlined"
																		label="Start Date *"
																		showTodayButton
																		value={timesheet.startDate ? moment(timesheet.startDate, "YYYY-MM-DD") : null}
																		variant="outlined"
																		disableFuture
																		onChange={(value) => onChange({ ...values, timesheet: { ...timesheet, startDate: value ? value.format("YYYY-MM-DD") : "" } })}
																		error={values.isEndDateTimeInvalid}
																		autoOk
																		okLabel=""
																	/>
																</Grid>
																<Grid item sm={6} xs={12}>
																	<KeyboardTimePicker
																		fullWidth
																		inputVariant="outlined"
																		label="Start Time *"
																		value={timesheet.startTime ? moment(timesheet.startTime, "HH:mm") : null}
																		onChange={(value) =>
																			onChange({
																				...values,
																				timesheet: {
																					...timesheet,
																					startTime:
																						value &&
																						typeof value.format === "function" &&
																						typeof value.isValid === "function" &&
																						value.isValid()
																							? value.format("HH:mm")
																							: ""
																				}
																			})
																		}
																		error={values.isEndDateTimeInvalid}
																		ampm
																		minutesStep={5}
																		keyboardIcon={<AccessTimeIcon fontSize="small" />}
																		KeyboardButtonProps={{ "aria-label": "change start time" }}
																	/>
																</Grid>

																<Grid item sm={6} xs={12}>
																	<DatePicker
																		format="Do MMMM YYYY"
																		fullWidth
																		inputVariant="outlined"
																		label="End Date *"
																		minDate={timesheet.startDate ? moment(timesheet.startDate, "YYYY-MM-DD").startOf("day") : null}
																		showTodayButton
																		value={timesheet.endDate ? moment(timesheet.endDate, "YYYY-MM-DD") : null}
																		variant="outlined"
																		onChange={(value) => onChange({ ...values, timesheet: { ...timesheet, endDate: value ? value.format("YYYY-MM-DD") : "" } })}
																		error={values.isEndDateTimeInvalid}
																		autoOk
																		okLabel=""
																	/>
																</Grid>
																<Grid item sm={6} xs={12}>
																	<KeyboardTimePicker
																		fullWidth
																		inputVariant="outlined"
																		label="End Time *"
																		value={timesheet.endTime ? moment(timesheet.endTime, "HH:mm") : null}
																		onChange={(value) =>
																			onChange({
																				...values,
																				timesheet: {
																					...timesheet,
																					endTime:
																						value &&
																						typeof value.format === "function" &&
																						typeof value.isValid === "function" &&
																						value.isValid()
																							? value.format("HH:mm")
																							: ""
																				}
																			})
																		}
																		error={values.isEndDateTimeInvalid}
																		ampm
																		minutesStep={5}
																		keyboardIcon={<AccessTimeIcon fontSize="small" />}
																		KeyboardButtonProps={{ "aria-label": "change end time" }}
																	/>
																	{values.isEndDateTimeInvalid && (
																		<Typography variant="body2" style={{ color: "#f44336", marginTop: 4 }}>
																			End date/time must be later than start
																		</Typography>
																	)}
																</Grid>
																<Grid item xs={12}>
																	<SelectInput
																		id="lunch_time"
																		label="Breaks"
																		options={[{ label: "None", value: "0" }, { label: "30 mins", value: "30" }, { label: "45 mins", value: "45" }, { label: "60 mins", value: "60" }]}
																		value={timesheet.lunchTime || "0"}
																		onChange={(v) => onChange({ ...values, timesheet: { ...timesheet, lunchTime: v } })}
																		noNone
																	/>
																</Grid>
																<Grid item xs={12}>
																	<TextField
																		id="timesheet_notes"
																		fullWidth
																		label="Notes"
																		multiline
																		rows="3"
																		value={timesheet.notes || ""}
																		variant="outlined"
																		onChange={(e) => onChange({ ...values, timesheet: { ...timesheet, notes: e.target.value } })}
																	/>
																</Grid>
															</Grid>
														</CardContent>
													</Card>
												)}

												{/* Timer status + notes while active. Start/Stop live next to the site field. */}
												{values.timesheet.toggle && (
													<div style={{ marginTop: 16 }}>
														{React.cloneElement(form, {
															showStartButton: false,
															showStopButton: false,
															onStopped: () => {
																// Push ?stopped=1 so back button returns to active view
																const basePath = siteId ? `${ADD_TIMESHEET_ROUTE}/${siteId}` : ADD_TIMESHEET_ROUTE;
																history.push(`${basePath}?stopped=1`);
															},
														})}
													</div>
												)}
											</>
										)}
									</>
								)}
							</Grid>
						</Grid>

						{/* Show Save/Cancel buttons for Admin timesheet form (admins only) */}
						{valuesAdmin.siteId && isAdmin && isTimesheetForm.current && (
							<FormActions className={classes.actions}>
								<FormSubmitButton
									disabled={!isValid || checkingOverlap}
									loading={checkingOverlap || !!valuesAdmin.isLoading}
									size="large"
									fullWidth={false}
									disableRipple={false}
									disableFocusRipple={false}
								>
									Save
								</FormSubmitButton>
								<div style={{ flex: 1 }}/>
								{/* Show Discard button when in review mode (stopping someone else's timesheet) */}
								{reviewMode && valuesAdmin.timesheet.id && (
									<FormButton 
										size="large" 
										disableRipple={false} 
										onClick={onDiscardAdmin}
									>
										Discard
									</FormButton>
								)}
								{!reviewMode && (
									<FormButton size="large" disableRipple={false} onClick={onCancel}>
										Cancel
									</FormButton>
								)}
							</FormActions>
						)}

						{/* Regular Add Timesheet - Save/Discard buttons */}
						{shouldShowInlineSaveActions && (
							<FormActions className={classes.actions}>
								<FormSubmitButton
									loading={values.isLoading || checkingOverlap}
									disabled={inlineSaveDisabled || checkingOverlap}
								>
									Save
								</FormSubmitButton>
								{inlineSaveDisabled && !values.isLoading && (
									<Typography variant="body2" color="textSecondary">
										{values.isEndDateTimeInvalid
											? "End date/time must be later than start."
											: (inlineSaveMissingFields.length > 0
												? `Complete: ${inlineSaveMissingFields.join(", ")}.`
												: "Complete all required fields to enable Save.")}
									</Typography>
								)}
								<div style={{ flex: 1 }}/>
								{reviewMode && (
									<FormButton 
										loading={values.isLoading} 
										disabled={values.isLoading} 
										onClick={onDiscard}
									>
										Discard
									</FormButton>
								)}
							</FormActions>
						)}
					</ValidatorForm>
					<StyledModal
						open={overlapDialogOpen}
						title="Overlapping time"
						onClose={onCloseOverlapDialog}
						onConfirm={onConfirmOverlapSave}
						onConfirmText="Save anyway"
					>
						{overlapMessage}
					</StyledModal>
					<PopupSnackbar message={values.errorMessage} coloured error onExited={() => {}} />
				</PageContent>
			</Page>
		);
	}
);
